import { fallback, http, createPublicClient, createWalletClient } from 'viem';
import * as chains from 'viem/chains';

/**
 * Helper function to add our onfinality api key to a public onfinality RPC url.
 */
const addOnfinalityApiKey = (rpcUrl, onfinalityApiKey) => {
  if (typeof onfinalityApiKey !== "string" || !onfinalityApiKey) return rpcUrl;

  // inject api key here because we don't want them in the store (user can modify urls of rpcs)
  return rpcUrl.replace(/^https:\/\/([A-z-]+)\.api\.onfinality\.io\/public-ws\/?$/, `https://$1.api.onfinality.io/ws?apikey=${onfinalityApiKey}`).replace(/^https:\/\/([A-z-]+)\.api\.onfinality\.io\/rpc\/?$/, `https://$1.api.onfinality.io/rpc?apikey=${onfinalityApiKey}`);
};

// viem chains benefit from multicall config & other viem goodies
const VIEM_CHAINS = Object.keys(chains).reduce((acc, curr) => {
  const chain = chains[curr];
  acc[chain.id] = chain;
  return acc;
}, {});
const chainsCache = new Map();
const clearChainsCache = evmNetworkId => {
  if (evmNetworkId) chainsCache.delete(evmNetworkId);else chainsCache.clear();
};
const getChainFromEvmNetwork = (evmNetwork, nativeToken, options = {}) => {
  if (!evmNetwork?.nativeToken?.id) throw new Error("Undefined native token");
  if (evmNetwork.nativeToken.id !== nativeToken.id) throw new Error("Native token mismatch");
  const {
    symbol,
    decimals
  } = nativeToken;
  if (!chainsCache.has(evmNetwork.id)) {
    const chainRpcs = evmNetwork.rpcs?.map(rpc => addOnfinalityApiKey(rpc.url, options.onFinalityApiKey)) ?? [];
    const viemChain = VIEM_CHAINS[Number(evmNetwork.id)] ?? {};
    const chain = {
      ...viemChain,
      id: Number(evmNetwork.id),
      name: evmNetwork.name ?? `EVM Chain ${evmNetwork.id}`,
      rpcUrls: {
        public: {
          http: chainRpcs
        },
        default: {
          http: chainRpcs
        }
      },
      nativeCurrency: {
        symbol,
        decimals,
        name: symbol
      }
    };
    chainsCache.set(evmNetwork.id, chain);
  }
  return chainsCache.get(evmNetwork.id);
};

const HTTP_BATCH_WAIT = 25;
const HTTP_BATCH_SIZE = 30;
const getTransportForEvmNetwork = (evmNetwork, options = {}) => {
  if (!evmNetwork.rpcs?.length) throw new Error("No RPCs found for EVM network");
  return fallback(evmNetwork.rpcs.map(rpc => http(addOnfinalityApiKey(rpc.url, options.onFinalityApiKey), {
    batch: {
      wait: HTTP_BATCH_WAIT,
      batchSize: HTTP_BATCH_SIZE
    },
    retryCount: 0
  })), {
    retryCount: 0
  });
};

const MUTLICALL_BATCH_WAIT = 25;
const MUTLICALL_BATCH_SIZE = 1000;

// cache to reuse previously created public clients
const publicClientCache = new Map();
const clearPublicClientCache = evmNetworkId => {
  clearChainsCache(evmNetworkId);
  if (evmNetworkId) publicClientCache.delete(evmNetworkId);else publicClientCache.clear();
};
const getEvmNetworkPublicClient = (evmNetwork, nativeToken, options = {}) => {
  const chain = getChainFromEvmNetwork(evmNetwork, nativeToken);
  if (!publicClientCache.has(evmNetwork.id)) {
    if (!evmNetwork.rpcs?.length) throw new Error("No RPCs found for EVM network");
    const batch = chain.contracts?.multicall3 ? {
      multicall: {
        wait: MUTLICALL_BATCH_WAIT,
        batchSize: MUTLICALL_BATCH_SIZE
      }
    } : undefined;
    const transport = getTransportForEvmNetwork(evmNetwork, options);
    publicClientCache.set(evmNetwork.id, createPublicClient({
      chain,
      transport,
      batch
    }));
  }
  return publicClientCache.get(evmNetwork.id);
};

const getEvmNetworkWalletClient = (evmNetwork, nativeToken, options = {}) => {
  const chain = getChainFromEvmNetwork(evmNetwork, nativeToken, {
    onFinalityApiKey: options.onFinalityApiKey
  });
  const transport = getTransportForEvmNetwork(evmNetwork, options);
  return createWalletClient({
    chain,
    transport,
    account: options.account
  });
};

class ChainConnectorEvm {
  #chaindataProvider;
  #onfinalityApiKey;
  constructor(chaindataProvider, options) {
    this.#chaindataProvider = chaindataProvider;
    this.#onfinalityApiKey = options?.onfinalityApiKey ?? undefined;
  }
  setOnfinalityApiKey(apiKey) {
    this.#onfinalityApiKey = apiKey;
    this.clearRpcProvidersCache();
  }
  async getPublicClientForEvmNetwork(evmNetworkId) {
    const network = await this.#chaindataProvider.evmNetworkById(evmNetworkId);
    if (!network?.nativeToken?.id) return null;
    const nativeToken = await this.#chaindataProvider.tokenById(network.nativeToken.id);
    if (!nativeToken) return null;
    return getEvmNetworkPublicClient(network, nativeToken, {
      onFinalityApiKey: this.#onfinalityApiKey
    });
  }
  async getWalletClientForEvmNetwork(evmNetworkId, account) {
    const network = await this.#chaindataProvider.evmNetworkById(evmNetworkId);
    if (!network?.nativeToken?.id) return null;
    const nativeToken = await this.#chaindataProvider.tokenById(network.nativeToken.id);
    if (!nativeToken) return null;
    return getEvmNetworkWalletClient(network, nativeToken, {
      onFinalityApiKey: this.#onfinalityApiKey,
      account
    });
  }
  clearRpcProvidersCache(evmNetworkId) {
    clearPublicClientCache(evmNetworkId);
  }
}

export { ChainConnectorEvm };
